import { getApiKey } from './env';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const DEFAULT_TIMEOUT_MS = 60_000;

function mergeHeaders(extra?: HeadersInit): Headers {
  const h = new Headers(extra);
  const key = getApiKey();
  if (key && !h.has('Authorization')) {
    h.set('Authorization', `Bearer ${key}`);
  }
  return h;
}

export async function apiFetch(
  url: string,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<Response> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...rest } = init;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      ...rest,
      signal: controller.signal,
      headers: mergeHeaders(rest.headers),
    });
    return res;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      throw new ApiError(`Request timed out (${timeoutMs}ms)`, 0);
    }
    throw e;
  } finally {
    clearTimeout(id);
  }
}

export async function apiJson<T>(url: string, init?: RequestInit & { timeoutMs?: number }): Promise<T> {
  const hasBody = init?.body != null && init.body !== '';
  const headers = mergeHeaders(init?.headers);
  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await apiFetch(url, {
    ...init,
    headers,
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    const msg =
      typeof body === 'object' && body !== null && 'error' in (body as object)
        ? String((body as { error?: string }).error)
        : res.statusText;
    throw new ApiError(msg || `HTTP ${res.status}`, res.status, body);
  }
  return res.json() as Promise<T>;
}
